import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Table } from 'primeng/table';
import { Observable, Subscription, take } from 'rxjs';
import { TemplateConstants } from '../constants/template-constants';

/**
 * Generic response interface for paginated list responses
 */
export interface PaginatedResponse<T> {
  continuationToken: string | null;
  items: T[];
}

/**
 * Base class for list components with pagination support
 * @template T The type of items in the list
 */
@Component({
  template: '' // Abstract component, no template
})
export abstract class BaseListComponent<T extends { id: string }> implements OnInit {
  @ViewChild('table') table!: Table;

  protected itemsDictionary: Map<string, T> = new Map<string, T>();
  protected items: T[] = new Array<T>();
  public displayItems?: T[] = new Array<T>();
  SelectedItem?: T;
  protected continuationToken?: string | null;
  
  ItemsPerPage: number = TemplateConstants.ItemsPerPageValue;
  HasMoreResults: boolean = false;
  AllowInteraction: boolean = false;
  IsInitialized: boolean = false;
  SelectionIndex?: number;
  LoadMoreText!: string;

  protected nextResultsText!: string;
  protected noMoreResultsText!: string;

  constructor(
    protected readonly transloco: TranslocoService,
    protected readonly cdr?: ChangeDetectorRef
  ) {}

  protected loadTranslations(): void {
    this.transloco.selectTranslate('List.loadNext', { maxPerPage: this.ItemsPerPage })
      .pipe(take(1))
      .subscribe(res => this.nextResultsText = res);
    this.transloco.selectTranslate('List.noMoreResults')
      .pipe(take(1))
      .subscribe(res => this.noMoreResultsText = res);
  }

  protected setLoadText(): void {
    if (this.HasMoreResults) {
      this.LoadMoreText = this.nextResultsText;
    } else {
      this.LoadMoreText = this.noMoreResultsText;
    }
  }

  ngOnInit(): void {
    this.loadTranslations();
    this.loadItems(() => {
      if (this.items.length !== 0) {
        this.itemClicked({ index: 0 });
      }
    });
  }

  protected onRefreshList(): void {
    this.AllowInteraction = false;
    if (this.SelectedItem) {
      this.SelectedItem = undefined;
    }
    this.items = new Array<T>();
    this.itemsDictionary = new Map<string, T>();
    this.continuationToken = undefined;
    this.HasMoreResults = false;
    this.loadItems();
  }

  protected onItemDeleted(item: T): void {
    if (!this.itemsDictionary.has(item.id)) {
      return;
    }
    let idx = 0;
    for (const [key, _] of this.itemsDictionary) {
      if (key === item.id) {
        this.items.splice(idx, 1);
        this.itemsDictionary.delete(item.id);
        this.updateDisplayItems();
        break;
      }
      idx++;
    }
    this.SelectionIndex = undefined;
    this.SelectedItem = undefined;
  }

  public itemClicked(event: any): void {
    let index = event.index;

    if (index < 0 || index >= this.items.length) {
      return;
    }
    this.SelectionIndex = index;
    this.SelectedItem = this.items[index];
    this.onItemSelected(this.items[index]);
  }

  public onRowSelected(item: T | T[] | undefined): void {
    if (!item || Array.isArray(item)) {
      return;
    }
    const index = this.items.indexOf(item);
    if (index === -1) {
      return;
    }
    this.itemClicked({ index });
  }

  public loadNextPageClicked(): void {
    if (!this.HasMoreResults) {
      return;
    }
    this.loadItems();
  }

  public refreshClicked(): void {
    if (!this.AllowInteraction) {
      return;
    }
    this.onRefreshList();
  }

  protected updateDisplayItems(): void {
    this.displayItems = this.items;
    if (this.cdr) {
      this.cdr.detectChanges();
    }
  }

  protected processResponse(response: PaginatedResponse<T>, callback?: () => void): void {
    let newItems = new Array<T>();
    this.displayItems = [];
    if (this.cdr) {
      this.cdr.detectChanges();
    }
    
    for (let item of response.items) {
      // Created items are immediately added to the list, so by storing their ID we can prevent duplicates
      if (!this.itemsDictionary.has(item.id)) {
        this.itemsDictionary.set(item.id, item);
        newItems.push(item);
      }
    }
    this.items.push(...newItems);
    this.updateDisplayItems();
    this.continuationToken = response.continuationToken;
    this.AllowInteraction = true;
    this.IsInitialized = true;
    this.HasMoreResults = (this.continuationToken !== null && this.continuationToken !== undefined);
    this.setLoadText();

    if (callback) {
      callback();
    }
    if (this.cdr) {
      this.cdr.detectChanges();
    }
  }

  // Abstract methods that child classes must implement
  protected abstract loadItems(callback?: () => void): void;
  protected abstract onItemSelected(item: T): void;
}

